import { useMemo } from 'react'
import { BarChartWrapper } from '../charts/BarChart'
import { formatMoney } from '../format'
import { buildDrillRsql } from '../drill'
import { WidgetFrame } from './WidgetFrame'
import { useWidgetData } from './useWidgetData'
import { WIDGET_KEY, type WidgetProps } from './types'
import { runExport, type ExportColumn } from '../../../lib/export'
import { aggregateCertifiedByJurisdiction, type JurisdictionCreditTotal } from '../reportsData'
import type { CreditValueByJurisdictionResult } from '../../../types/insights'

const WIDGET_KEY_SELF = WIDGET_KEY.creditValueByJurisdictionFy

/** Same disclosed constant as `CreditValueByJurisdictionFyWidget` — every certified-value fact at launch is USD. */
const REPORTING_CURRENCY = 'USD'

const EXPORT_COLUMNS: ExportColumn<JurisdictionCreditTotal>[] = [
  { id: 'jurisdiction', header: 'Jurisdiction', toText: (r) => r.label },
  { id: 'total', header: `Certified total (${REPORTING_CURRENCY})`, toText: (r) => formatMoney(String(r.total), REPORTING_CURRENCY) },
  { id: 'status', header: 'Status', toText: (r) => (r.total > 0 ? 'Has certified credit' : 'No certified credit yet') },
]

interface JurisdictionRow {
  jurisdictionLabel: string
  total: number
}

/**
 * Credits by jurisdiction & status — a SECOND view over the exact same `credit-value-by-
 * jurisdiction-fy` payload `CreditValueByJurisdictionFyWidget` already renders (own independent
 * fetch of the same endpoint, same pattern `ReportsScreen` already uses to reuse a dashboard
 * widget's data on a second screen) — no new backend read.
 *
 * "Status" here is the assembler's own AC25 zero-fill semantics (`CreditValueAssembler.assemble`):
 * a jurisdiction with a published Program but zero certified facts still gets a bucket, at $0 — so
 * "has certified credit" vs. "no certified credit yet" is a real backend-emitted distinction, not a
 * client-side guess. A richer "certified vs. transferred by jurisdiction" cross (this tile's
 * originally-scoped alternative) is NOT built this pass: `monetization-outcomes`
 * (`OutcomePathBucket`) carries no jurisdiction dimension today, and giving it one would mean a new
 * cross-module join in `module.monetization` — a real backend change, not a frontend-only reuse, so
 * it is disclosed here rather than built. A time-series view is also not built: `fiscalYearId` has
 * no label-lookup endpoint yet (same gap `CreditValueByJurisdictionFyWidget` already discloses), so
 * it is an opaque id, not a reliable chronological axis.
 */
export function CreditsByJurisdictionStatusWidget({ filter, skipRefetch, onCrossFilter, onDrill }: WidgetProps) {
  const { status, data, message, retry } = useWidgetData<CreditValueByJurisdictionResult>(WIDGET_KEY_SELF, filter, skipRefetch)

  const buckets = useMemo(() => (Array.isArray(data?.buckets) ? data.buckets : []), [data])
  const totals = useMemo(() => aggregateCertifiedByJurisdiction(buckets), [buckets])

  const withCredit = useMemo(() => totals.filter((t) => t.total > 0), [totals])
  const pending = useMemo(() => totals.filter((t) => t.total === 0), [totals])

  const rows: JurisdictionRow[] = withCredit.map((t) => ({ jurisdictionLabel: t.label, total: t.total }))

  const frameState = status === 'ok' && totals.length === 0 ? 'empty' : status

  return (
    <WidgetFrame
      title="Credits by jurisdiction & status"
      icon="dollar-sign"
      state={frameState}
      message={message}
      onRetry={retry}
      drill={{ label: 'View filings', onClick: () => onDrill(buildDrillRsql(filter)) }}
      exportAction={totals.length > 0 ? { onClick: () => runExport('csv', 'Credits by jurisdiction and status', totals, EXPORT_COLUMNS) } : undefined}
    >
      {rows.length > 0 ? (
        <BarChartWrapper<JurisdictionRow>
          title="Credits by jurisdiction & status"
          data={rows}
          categoryKey="jurisdictionLabel"
          categoryLabel="Jurisdiction"
          series={[{ key: 'total', label: 'Certified value' }]}
          valueFormatter={(v) => formatMoney(String(v), REPORTING_CURRENCY)}
          onBarClick={(row) => {
            const match = withCredit.find((t) => t.label === row.jurisdictionLabel)
            if (match) onCrossFilter('jurisdiction', match.jurisdiction)
          }}
        />
      ) : (
        <p style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)', margin: 0 }}>
          No jurisdiction has certified credit yet for the current filter.
        </p>
      )}
      {pending.length > 0 ? (
        <p style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)', margin: 'var(--iw-space-2) 0 0' }}>
          {pending.length} jurisdiction{pending.length === 1 ? '' : 's'} with no certified credit yet:{' '}
          {pending.map((p) => p.label).join(', ')}.
        </p>
      ) : null}
    </WidgetFrame>
  )
}
